import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Meta Conversions API (CAPI) contracts - ADR-0066.
 *
 * Ad-account credentials + routing live in the Secrets Manager (ADR-0018), so
 * there is no ad-account DTO here. These schemas cover the read-only config
 * status the admin screen renders, the delivery log, and the "send test event"
 * action. Event mapping (CRM state -> Meta event) is code-driven and lives in
 * the API resolver (meta-capi-event-resolver.ts), not in shared schemas.
 */

/** Delivery outcome for one outbound CAPI event. */
export const CAPI_EVENT_STATUSES = ['PENDING', 'SENT', 'FAILED', 'SKIPPED'] as const;
export const CapiEventStatusSchema = z.enum(CAPI_EVENT_STATUSES);
export type CapiEventStatus = z.infer<typeof CapiEventStatusSchema>;

// ===========================================================================
// Delivery log (meta_capi_events)
// ===========================================================================

export const MetaCapiEventLogItemSchema = z.object({
  id: UuidSchema,
  leadId: UuidSchema,
  leadReference: z.string().nullable(),
  adAccountId: z.string().nullable(),
  capiEventName: z.string(),
  eventId: z.string(),
  status: CapiEventStatusSchema,
  skipReason: z.string().nullable(),
  attempts: z.number().int(),
  responseStatus: z.number().int().nullable(),
  fbTraceId: z.string().nullable(),
  error: z.string().nullable(),
  sentAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type MetaCapiEventLogItem = z.infer<typeof MetaCapiEventLogItemSchema>;

export const MetaCapiEventLogDetailSchema = MetaCapiEventLogItemSchema.extend({
  /// The exact body posted to Meta. PII is already SHA-256 hashed, so this is
  /// safe to surface in the admin log.
  requestPayload: z.record(z.string(), z.unknown()).nullable(),
  responseBody: z.string().nullable(),
  sourceOutboxEventId: z.string().nullable(),
});
export type MetaCapiEventLogDetail = z.infer<typeof MetaCapiEventLogDetailSchema>;

export const MetaCapiEventLogListQuerySchema = PaginationQuerySchema.extend({
  status: CapiEventStatusSchema.optional(),
  adAccountId: z.string().trim().max(64).optional(),
  capiEventName: z.string().trim().max(64).optional(),
  leadId: UuidSchema.optional(),
});
export type MetaCapiEventLogListQuery = z.infer<typeof MetaCapiEventLogListQuerySchema>;

export const MetaCapiEventLogListResponseSchema = paginatedResponseSchema(
  MetaCapiEventLogItemSchema,
);
export type MetaCapiEventLogListResponse = z.infer<typeof MetaCapiEventLogListResponseSchema>;

// ===========================================================================
// Cohort summary (meta_capi_events grouped by capi_event_name)
// ===========================================================================

/**
 * The CAPI event cohorts the CURRENT implementation sends to Meta, in funnel
 * order. Mirrors the code resolver's `CapiEvent` union (meta-capi-event-
 * resolver.ts). The retired lowercase / `Active` / `ready_to_book` names from
 * the old DB-map scheme (cut over ~2026-10-16) are intentionally excluded - the
 * summary covers the new cohorts only.
 */
export const META_CAPI_NEW_COHORTS = [
  'Lead',
  'Converted',
  'Qualified',
  'PartialQualified',
  'NonQualified',
] as const;
export type MetaCapiCohort = (typeof META_CAPI_NEW_COHORTS)[number];

/** One row of the cohort summary table: a cohort + its delivery counts. */
export const MetaCapiCohortSummaryItemSchema = z.object({
  cohort: z.string(),
  /// Events delivered to Meta successfully (status = SENT).
  sent: z.number().int(),
  /// All attempts for this cohort (SENT + FAILED + SKIPPED + PENDING).
  total: z.number().int(),
});
export type MetaCapiCohortSummaryItem = z.infer<typeof MetaCapiCohortSummaryItemSchema>;

export const MetaCapiCohortSummaryResponseSchema = z.object({
  cohorts: z.array(MetaCapiCohortSummaryItemSchema),
});
export type MetaCapiCohortSummaryResponse = z.infer<typeof MetaCapiCohortSummaryResponseSchema>;

// ===========================================================================
// Config status (read-only view of what the Secrets Manager currently holds).
// Never carries the token itself - only whether one is present.
// ===========================================================================

export const MetaCapiAccountConfigSchema = z.object({
  accountId: z.string(),
  label: z.string().nullable(),
  datasetId: z.string().nullable(),
  hasAccessToken: z.boolean(),
  testEventCode: z.string().nullable(),
});
export type MetaCapiAccountConfig = z.infer<typeof MetaCapiAccountConfigSchema>;

export const MetaCapiConfigStatusSchema = z.object({
  enabled: z.boolean(),
  apiVersion: z.string(),
  accounts: z.array(MetaCapiAccountConfigSchema),
});
export type MetaCapiConfigStatus = z.infer<typeof MetaCapiConfigStatusSchema>;

// ===========================================================================
// Send test event
// ===========================================================================

export const SendCapiTestEventRequestSchema = z.object({
  /// The ad-account id (matches lead.accountId / the Secrets key segment).
  /// Omit to auto-pick: the `default` account if configured, else the first
  /// fully-configured account.
  accountId: z.string().trim().min(1).max(64).optional(),
  /// Defaults to a benign "Lead" probe on the server when omitted.
  eventName: z.string().trim().min(1).max(64).optional(),
});
export type SendCapiTestEventRequest = z.infer<typeof SendCapiTestEventRequestSchema>;

export const SendCapiTestEventResponseSchema = z.object({
  ok: z.boolean(),
  httpStatus: z.number().int().nullable(),
  fbTraceId: z.string().nullable(),
  responseBody: z.string().nullable(),
});
export type SendCapiTestEventResponse = z.infer<typeof SendCapiTestEventResponseSchema>;
